import {useCallback, useRef, useState} from 'react';
import {call, delay, fork, select, takeLatest} from 'redux-saga/effects';
import {useSelector} from "react-redux";

import {channel} from "redux-saga";
import {actionEffects as actions} from "../store/actions";
import {selectCommunities, selectLoggedIn, selectProfile, selectStaleCommunities} from "../store/selectors";
import {useSagaCallback, useSagaEffect} from '../utils/sagas';
import {MINUTE_IN_MILLIS} from "../utils/time";
import {apiGet, apiPost, apiPostForm, useGetRequestData} from './client';

export const useCommunitySubscribe = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (names) {
        const loggedIn = yield select(selectLoggedIn);
        const {subscribed} = yield select(selectProfile);
        const newNames = [];
        const oldNames = [];
        for (const name of names) {
            const index = subscribed.indexOf(name);
            const wasSubscribed = index !== -1;
            (wasSubscribed ? oldNames : newNames).push(name);
        }
        yield actions.settingsChanged({
            subscribed: [
                ...subscribed.filter(element => !oldNames.includes(element)),
                ...newNames
            ]
        });
        yield fork(function* () {
            if (loggedIn) {
                for (const name of newNames) {
                    yield call(apiPost, `/api/v2/community/subscribe`, {community: name});
                }
                for (const name of oldNames) {
                    yield call(apiPost, `/api/v2/community/unsubscribe`, {community: name});
                }
            }
            yield actions.feedsCleared('home');
        })
    }, []));
};

export const useModeratorList = (feedbackCh, community) =>
    useGetRequestData(feedbackCh, community && `/api/v2/community/moderators.json?community=${community}`)?.moderators;

export const useCommunityStyle = (feedbackCh, community) =>
    useGetRequestData(feedbackCh, community && `/api/v2/community/style.json?community=${community}`)?.communities[0];

export const useCommunitySettings = (feedbackCh, community, setSettings) => {
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/community/settings.json?community=${community}`;
        const data = yield call(apiGet, url);
        const settings = data.communities[0];
        setSettings({
            displayName: settings.displayName,
            tagline: settings.tagline,
            description: settings.description,
            metaTags: settings.metaTags,
            minScorePosts: settings.minScorePosts,
            minScoreComments: settings.minScoreComments,
            minScoreDownPosts: settings.minScoreDownPosts,
            minScoreDownComments: settings.minScoreDownComments,
            visibility: settings.visibility,
            defaultPostType: settings.defaultPostType,
            defaultCommentSort: settings.defaultCommentSort,
            allowTextPosts: settings.allowTextPosts,
            allowImagePosts: settings.allowImagePosts,
            allowLinkPosts: settings.allowLinkPosts,
            contentFilters: settings.contentFilters,
            allowPostEditing: settings.allowPostEditing,
            allowCommentEditing: settings.allowCommentEditing,
            allowComments: settings.allowComments,
            allowChildComments: settings.allowChildComments,
            publicLogs: settings.publicLogs,
            obsfucatedLogs: settings.obsfucatedLogs,
            notifyReportConclusion: settings.notifyReportConclusion,
            topics: settings.topics,
            banLogs: settings.banLogs,
            appSafe: settings.appSafe,
        });
    }, [community]));
};

export const useSaveSettings = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (community, settings) {
        return yield call(apiPostForm, `/api/v2/community/settings.json`, {
            community,
            displayName: settings.displayName,
            tagline: settings.tagline,
            description: settings.description,
            metaTags: settings.metaTags,
            minScorePosts: settings.minScorePosts,
            minScoreComments: settings.minScoreComments,
            minScoreDownPosts: settings.minScoreDownPosts,
            minScoreDownComments: settings.minScoreDownComments,
            visibility: settings.visibility,
            defaultPostType: settings.defaultPostType,
            defaultCommentSort: settings.defaultCommentSort,
            allowTextPosts: settings.allowTextPosts,
            allowImagePosts: settings.allowImagePosts,
            allowLinkPosts: settings.allowLinkPosts,
            contentFilters: settings.contentFilters,
            allowPostEditing: settings.allowPostEditing,
            allowCommentEditing: settings.allowCommentEditing,
            allowComments: settings.allowComments,
            allowChildComments: settings.allowChildComments,
            publicLogs: settings.publicLogs,
            obsfucatedLogs: settings.obsfucatedLogs,
            notifyReportConclusion: settings.notifyReportConclusion,
            topics: JSON.stringify(settings.topics),
            banLogs: settings.banLogs,
            appSafe: settings.appSafe,
        });
    }, []))
};

export const useCommunityFilter = (feedbackCh, community, setCode) => {
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/community/filter.json?community=${community}`;
        const data = yield call(apiGet, url);
        setCode(data.communities[0].filter);
    }, [community, setCode]));
};

export const useCommunityStylesheet = (feedbackCh, community, setCode) => {
    useSagaEffect(feedbackCh, useCallback(function* () {
        const url = `/api/v2/community/stylesheet.json?community=${community}`;
        const data = yield call(apiGet, url);
        setCode(data.communities[0].stylesheet);
    }, [community, setCode]));
};

export const useCreateCommunity = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (name, displayName, description, politics, nsfw) {
        return yield call(apiPost, `/api/v2/action/create_community.json`, {
            name,
            displayName,
            description,
            politics: !politics,
            nsfw: !nsfw
        });
    }, []))
};

export const useLogs = (feedbackCh, community, page, type, moderator) =>
    useGetRequestData(feedbackCh, community && `/api/v2/community/logs.json?community=${community}${page ? `&page=${page}` : ''}${type ? `&type=${type}` : ''}${moderator ? `&moderator=${moderator}` : ''}`)?.logs;

export const useSuggestedCommunities = (feedbackCh) =>
    useGetRequestData(feedbackCh, `/api/v2/community/suggestions.json?appSafe=false`)?.topics;

export const loadCommunities = function* (names) {
    const url = '/api/v2/community/community.json';
    const { communities} = yield call(apiGet, url, { communities: names.join(',') });
    yield actions.communitiesLoaded(communities, Date.now() + 2 * MINUTE_IN_MILLIS);
};

/**
 * useCommunities returns the full list of communities requested by names.
 * Communities that are not already loaded will show up as null and will be requested from API.
 * Communities that were loaded more than 2 minutes ago will be refreshed from API.
 * @param feedbackCh
 * @param names {string[]}
 * @returns {object[]}
 */
export const useCommunities = (feedbackCh, names) => {
    useSagaEffect(feedbackCh, useCallback(function* () {
        const staleNames = yield select(selectStaleCommunities(names, Date.now()));
        if (staleNames.length > 0) {
            yield call(loadCommunities, staleNames);
        }
    }, [names.join(',')]));
    return useSelector((state) => selectCommunities(state, names));
};

export const useReloadCommunity = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (names) {
        yield call(loadCommunities, names);
    }, []))
};

export const useAllCommunities = (feedbackCh, sort, topic, showPolitics) => {
    const actionChRef = useRef(null);
    if (!actionChRef.current) {
        actionChRef.current = channel();
    }
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [names, setNames] = useState([]);
    useSagaEffect(feedbackCh, useCallback(function* () {
        setLoading(true);
        let page = 1;
        try {
            const {communities, has_more_entries} = yield call(apiGet, `/api/v2/community/communities.json`, { sort, topic, page, showPolitics });
            yield actions.communitiesLoaded(communities, Date.now() + 2 * MINUTE_IN_MILLIS);
            setNames(communities.map(({name}) => name));
            setHasMore(has_more_entries);
        } finally {
            setLoading(false);
        }
        yield takeLatest(actionChRef.current, function* ({ type }) {
            switch (type) {
                case 'refresh':
                    page = 1;
                    setNames([]);
                    // fall through
                case 'loadMore':
                    setLoading(true);
                    page += 1;
                    try {
                        const {communities, has_more_entries} = yield call(apiGet, `/api/v2/community/communities.json`, { sort, topic, page, showPolitics });
                        yield actions.communitiesLoaded(communities, Date.now() + 2 * MINUTE_IN_MILLIS);
                        setNames((names) => [...names, ...communities.map(({name}) => name)]);
                        setHasMore(has_more_entries);
                    } finally {
                        setLoading(false);
                    }
                    break;
                default:
                    throw new Error(type);
            }
        });
    }, [feedbackCh, sort, topic, showPolitics]));
    const communities = useSelector((state) => selectCommunities(state, names));
    const loadMore = useCallback(() => actionChRef.current.put({ type: 'loadMore' }), []);
    return { communities, loading, loadMore: !loading && hasMore ? loadMore : null };
};

export const useCommunitySearch = (feedbackCh, query) => {
    const [names, setNames] = useState(null);
    useSagaEffect(feedbackCh, useCallback(function* () {
        if (query) {
            yield delay(100);
            const {communities} = yield call(apiGet, `/api/v2/community/search.json?query=${query}`);
            yield actions.communitiesLoaded(communities, Date.now() + 2 * MINUTE_IN_MILLIS);
            setNames(communities.map(({name}) => name));
        }
    }, [query]));
    return useSelector((state) => names && selectCommunities(state, names));
};

export const useSaveFilter = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (community, filter) {
        return yield call(apiPost, `/api/v2/community/set_filter.json`, {
            community,
            filter
        });
    }, []))
};

export const useSaveStylesheet = (feedbackCh) => {
    return useSagaCallback(feedbackCh, useCallback(function* (community, stylesheet) {
        const data = {community};
        if (stylesheet != null) data.stylesheet = stylesheet;
        return yield call(apiPost, `/api/v2/community/set_stylesheet.json`, data);
    }, []))
};
